import { API_CONFIG, AUTH_API_PATHS, HTTP_STATUS } from '../constants/api'
import {
  clearAuthSession,
  getAccessToken,
  getRefreshToken,
  sessionFromTokens,
  writeAuthSession,
} from '../helpers/auth'
import type { ApiAuthTokens } from './types'

export class ApiError extends Error {
  status: number
  body: unknown

  constructor(message: string, status: number, body: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  /** Attach Bearer access token from the stored auth session. */
  auth?: boolean
  /** Skip 401 → refresh → retry (used for the refresh call itself). */
  skipRefresh?: boolean
}

let refreshInFlight: Promise<boolean> | null = null

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(path.replace(/^\//, ''), `${API_CONFIG.baseUrl.replace(/\/$/, '')}/`)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue
      url.searchParams.set(key, String(value))
    }
  }
  return url.toString()
}

function errorMessage(status: number, payload: unknown): string {
  if (
    typeof payload === 'object' &&
    payload &&
    'message' in payload &&
    (typeof (payload as { message: unknown }).message === 'string' ||
      Array.isArray((payload as { message: unknown }).message))
  ) {
    return Array.isArray((payload as { message: unknown }).message)
      ? (payload as { message: string[] }).message.join(', ')
      : String((payload as { message: string }).message)
  }
  return `Request failed (${status})`
}

async function refreshStoredSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearAuthSession()
      return false
    }

    try {
      const tokens = await apiRequest<ApiAuthTokens>(AUTH_API_PATHS.refresh, {
        method: 'POST',
        body: { refreshToken },
        skipRefresh: true,
      })
      writeAuthSession(sessionFromTokens(tokens))
      return true
    } catch (error) {
      if (error instanceof ApiError && error.status === HTTP_STATUS.unauthorized) {
        clearAuthSession()
      }
      return false
    }
  })().finally(() => {
    refreshInFlight = null
  })

  return refreshInFlight
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { query, body, headers, auth, skipRefresh, ...rest } = options
  const token = auth ? getAccessToken() : null

  const response = await fetch(buildUrl(path, query), {
    credentials: 'include',
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  const text = await response.text()
  const payload = text ? safeJson(text) : null

  if (
    response.status === HTTP_STATUS.unauthorized &&
    auth &&
    !skipRefresh &&
    path !== AUTH_API_PATHS.refresh
  ) {
    const refreshed = await refreshStoredSession()
    if (refreshed) {
      return apiRequest<T>(path, { ...options, skipRefresh: true })
    }
  }

  if (!response.ok) {
    throw new ApiError(errorMessage(response.status, payload), response.status, payload)
  }

  return payload as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}
